import React, { useCallback, useEffect, useMemo } from 'react';
import { useMtmFilterStore, useMtmColumnStore, useUIStore, useMtmDataStore, useMtmStore, useCurrencyDisplayStore } from '@/stores';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { BaseFilterSidebar } from '@/components/ui/BaseFilterSidebar';
import { CollapsibleFilterSection } from '@/features/inventory/components/CollapsibleFilterSection';
import { MtmColumnManagerPanel } from './MtmColumnManagerPanel';
import { NumericRangeFilter } from '../NumericRangeFilter';
import { MTM_FILTER_ID_TO_COLUMN_ID } from '@/utils/trades/filter-column-mapping';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { exportMtmToCSV } from '@/utils/trades/mtm-csv-export';
import { clsx } from 'clsx';
import { sortFiltersByColumnOrder as sortFilters } from '@/utils/filter-utils';
import { COLLATERAL_TYPE_LABELS, TRADE_STATUS_LABELS } from '@/types';
import {
  TRADE_AGREEMENT_TYPE_OPTIONS,
  TRADE_COLLATERAL_TYPE_OPTIONS,
  TRADE_CURRENCY_OPTIONS,
  TRADE_STATUS_OPTIONS,
  TRADE_TERM_TYPE_OPTIONS,
  TRADE_TYPE_OPTIONS,
} from '@/features/trades/constants/filter-options';

interface MtmFilterSidebarProps {
  isDataLoading?: boolean;
  hasError?: boolean;
}

type SidebarTab = 'filters' | 'columns';

type FilterDef = {
  id: string;
  label: string;
};

const sortFiltersByColumnOrder = (filters: FilterDef[], columns: { id: string }[]) =>
  sortFilters(filters, columns, MTM_FILTER_ID_TO_COLUMN_ID);

const MOCK_STATUSES = TRADE_STATUS_OPTIONS;
const MOCK_TRADE_TYPES = TRADE_TYPE_OPTIONS;
const MOCK_COLLATERAL_TYPES = TRADE_COLLATERAL_TYPE_OPTIONS;
const MOCK_AGREEMENT_TYPES = TRADE_AGREEMENT_TYPE_OPTIONS;
const MOCK_TERM_TYPES = TRADE_TERM_TYPE_OPTIONS;
const MOCK_CURRENCIES = TRADE_CURRENCY_OPTIONS;

/**
 * Filter order matches table column order (left-to-right). The Counterparty filter
 * is intentionally absent on MTM — the active subtab fixes the counterparty (LR-394).
 */
const FILTER_DEFS: FilterDef[] = [
  { id: 'statuses', label: 'Status' },
  { id: 'symbols', label: 'Security' },
  { id: 'agreementTypes', label: 'Agreement Type' },
  { id: 'tradeTypes', label: 'Trade Type' },
  { id: 'collateralTypes', label: 'Collateral Type' },
  { id: 'termTypes', label: 'Trade Term' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'grossPrice', label: 'Trade Gross Price' },
  { id: 'contractValue', label: 'Trade Margin Value' },
  { id: 'margin', label: 'Margin %' },
  // LR-474: '(USD)' dropped — see FilterSidebar (the figures are per-row ccy).
  { id: 'dailyEarnings', label: 'Daily Earnings' },
  { id: 'tradeDate', label: 'Trade Date' },
  { id: 'settlementDate', label: 'Settlement Date' },
  { id: 'maturityDate', label: 'Maturity Date' },
  // LR-474 (D3): filters the SECURITY (quotation) currency; the settlement-ccy
  // filter lives in the aggregate-bar currency pill, not the sidebar.
  { id: 'currencies', label: 'Security Currency' },
  { id: 'createdAt', label: 'Created At' },
  { id: 'updatedAt', label: 'Updated At' },
];

export const MtmFilterSidebar: React.FC<MtmFilterSidebarProps> = () => {
  const localMtmFilters = useMtmFilterStore((state) => state.localMtmFilters);
  const setLocalMtmFilters = useMtmFilterStore((state) => state.setLocalMtmFilters);
  const clearLocalMtmFilters = useMtmFilterStore((state) => state.clearLocalMtmFilters);
  const expandedFilterIds = useUIStore((state) => state.expandedFilterIds);
  const expandFilter = useUIStore((state) => state.expandFilter);
  const collapseFilter = useUIStore((state) => state.collapseFilter);
  const expandAllFilters = useUIStore((state) => state.expandAllFilters);
  const collapseAllFilters = useUIStore((state) => state.collapseAllFilters);
  const focusedFilterId = useUIStore((state) => state.focusedFilterId);
  const setFocusedFilterId = useUIStore((state) => state.setFocusedFilterId);
  const filterSidebarCollapsed = useUIStore((state) => state.filterSidebarCollapsed);
  const activeTab = useUIStore((s) => s.sidebarActiveTab) as SidebarTab;
  const setSidebarActiveTab = useUIStore((s) => s.setSidebarActiveTab);
  const setColumnsTabActive = useUIStore((state) => state.setColumnsTabActive);

  const columnConfig = useMtmColumnStore((state) => state.columns);
  const setLocateColumnRequest = useUIStore((state) => state.setLocateColumnRequest);
  const mtmRows = useMtmDataStore((state) => state.mtmRows);
  const mtmExportRows = useMtmDataStore((state) => state.mtmExportRows);
  const priceOverrides = useMtmStore((state) => state.priceOverrides);
  const excludedIds = useMtmStore((state) => state.excludedIds);
  const repoExcludedIds = useMtmStore((state) => state.repoExcludedIds);
  const baseCurrency = useCurrencyDisplayStore((state) => state.baseCurrency);
  // LR-538: the grid's edit state, published by MtmTable — needed so the export's synthetic
  // column totals match the on-screen footer row-for-row.
  const invalidIds = useMtmDataStore((state) => state.invalidIds);
  const noGmraAgreement = useMtmDataStore((state) => state.noGmraAgreement);

  // Security (symbol) options from the live MTM book (before sidebar filters), so
  // the list stays complete regardless of the current selection (LR-469).
  const securityOptions = useMemo(
    () => Array.from(new Set(mtmRows.map((t) => t.symbol_id).filter(Boolean))).sort(),
    [mtmRows],
  );

  // Security-currency options from the live MTM book, same pattern as
  // securityOptions (LR-474 quick-win — the static mock list never surfaced
  // currencies that only exist in real API data).
  const currencyOptions = useMemo(() => {
    const live = Array.from(
      new Set(mtmRows.map((t) => (t.currency || '').toUpperCase()).filter(Boolean)),
    ).sort();
    return live.length > 0 ? live : MOCK_CURRENCIES;
  }, [mtmRows]);

  const handleExport = useCallback(() => {
    exportMtmToCSV(
      mtmExportRows,
      priceOverrides,
      excludedIds,
      columnConfig,
      baseCurrency,
      repoExcludedIds,
      { noGmraAgreement },
      invalidIds,
    );
  }, [
    mtmExportRows,
    priceOverrides,
    excludedIds,
    columnConfig,
    baseCurrency,
    repoExcludedIds,
    noGmraAgreement,
    invalidIds,
  ]);

  // Rows the export will actually emit: the bucket's selection scope minus rows unchecked in their
  // own bucket — stock L/B via excludedIds, repo/reverse via repoExcludedIds (LR-548).
  const exportableCount = useMemo(
    () =>
      mtmExportRows.filter((r) => {
        const isRepo = r.trade_type === 'repo' || r.trade_type === 'reverse_repo';
        return isRepo ? !repoExcludedIds.has(r.id) : !excludedIds.has(r.id);
      }).length,
    [mtmExportRows, excludedIds, repoExcludedIds],
  );

  const handleTabChange = useCallback((tab: SidebarTab) => {
    setSidebarActiveTab(tab);
    setColumnsTabActive(tab === 'columns');
  }, [setSidebarActiveTab, setColumnsTabActive]);

  // Auto-clear focus after 2s
  useEffect(() => {
    if (focusedFilterId == null) return;
    const t = setTimeout(() => setFocusedFilterId(null), 2000);
    return () => clearTimeout(t);
  }, [focusedFilterId, setFocusedFilterId]);

  const updateFilter = useCallback((key: keyof typeof localMtmFilters, value: unknown) => {
    setLocalMtmFilters({
      ...localMtmFilters,
      [key]: value,
    });
  }, [localMtmFilters, setLocalMtmFilters]);

  const clearAllFilters = useCallback(() => {
    // LR-474 r1 #2 (D3): clear the SIDEBAR's filters but preserve the pill's
    // settlement-ccy selection — the sidebar badge never counts it and the
    // pill has its own Select all. clearLocalMtmFilters() would wipe it.
    const current = useMtmFilterStore.getState().localMtmFilters;
    clearLocalMtmFilters();
    if (current.settlementCurrencies.length > 0) {
      setLocalMtmFilters({
        ...useMtmFilterStore.getState().localMtmFilters,
        settlementCurrencies: current.settlementCurrencies,
      });
    }
  }, [clearLocalMtmFilters, setLocalMtmFilters]);

  const hasActiveFilter = useCallback((filterId: string): boolean => {
    switch (filterId) {
      case 'statuses': return localMtmFilters.statuses.length > 0;
      case 'symbols': return localMtmFilters.symbols.length > 0;
      case 'tradeTypes': return localMtmFilters.tradeTypes.length > 0;
      case 'collateralTypes': return localMtmFilters.collateralTypes.length > 0;
      case 'agreementTypes': return localMtmFilters.agreementTypes.length > 0;
      case 'termTypes': return localMtmFilters.termTypes.length > 0;
      case 'currencies': return localMtmFilters.currencies.length > 0;
      case 'tradeDate': return !!(localMtmFilters.tradeDateFrom || localMtmFilters.tradeDateTo);
      case 'settlementDate': return !!(localMtmFilters.settlementDateFrom || localMtmFilters.settlementDateTo);
      case 'maturityDate': return !!(localMtmFilters.maturityDateFrom || localMtmFilters.maturityDateTo);
      case 'quantity': return localMtmFilters.quantityMin !== null || localMtmFilters.quantityMax !== null;
      case 'contractValue': return localMtmFilters.contractValueMin !== null || localMtmFilters.contractValueMax !== null;
      case 'margin': return localMtmFilters.marginMin !== null || localMtmFilters.marginMax !== null;
      case 'grossPrice': return localMtmFilters.grossPriceMin !== null || localMtmFilters.grossPriceMax !== null;
      case 'dailyEarnings': return localMtmFilters.dailyEarningsMin !== null || localMtmFilters.dailyEarningsMax !== null;
      case 'createdAt': return !!(localMtmFilters.createdAtFrom || localMtmFilters.createdAtTo);
      case 'updatedAt': return !!(localMtmFilters.updatedAtFrom || localMtmFilters.updatedAtTo);
      default: return false;
    }
  }, [localMtmFilters]);

  const activeFilterCount =
    (localMtmFilters.statuses.length > 0 ? 1 : 0) +
    (localMtmFilters.symbols.length > 0 ? 1 : 0) +
    (localMtmFilters.tradeTypes.length > 0 ? 1 : 0) +
    (localMtmFilters.collateralTypes.length > 0 ? 1 : 0) +
    (localMtmFilters.agreementTypes.length > 0 ? 1 : 0) +
    (localMtmFilters.termTypes.length > 0 ? 1 : 0) +
    (localMtmFilters.currencies.length > 0 ? 1 : 0) +
    (localMtmFilters.tradeDateFrom || localMtmFilters.tradeDateTo ? 1 : 0) +
    (localMtmFilters.settlementDateFrom || localMtmFilters.settlementDateTo ? 1 : 0) +
    (localMtmFilters.maturityDateFrom || localMtmFilters.maturityDateTo ? 1 : 0) +
    (localMtmFilters.quantityMin !== null || localMtmFilters.quantityMax !== null ? 1 : 0) +
    (localMtmFilters.contractValueMin !== null || localMtmFilters.contractValueMax !== null ? 1 : 0) +
    (localMtmFilters.marginMin !== null || localMtmFilters.marginMax !== null ? 1 : 0) +
    (localMtmFilters.grossPriceMin !== null || localMtmFilters.grossPriceMax !== null ? 1 : 0) +
    (localMtmFilters.dailyEarningsMin !== null || localMtmFilters.dailyEarningsMax !== null ? 1 : 0) +
    (localMtmFilters.createdAtFrom || localMtmFilters.createdAtTo ? 1 : 0) +
    (localMtmFilters.updatedAtFrom || localMtmFilters.updatedAtTo ? 1 : 0);

  const renderMultiSelect = (
    filterKey: keyof typeof localMtmFilters,
    options: readonly string[],
    placeholder: string,
    searchPlaceholder: string,
    optionLabels?: Record<string, string>,
  ) => (
    <MultiSelectDropdown
      options={[...options]}
      selectedValues={localMtmFilters[filterKey] as string[]}
      onChange={(values) => updateFilter(filterKey, values)}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      pinSelectedToTop={true}
      showClearAll={false}
      optionLabels={optionLabels}
    />
  );

  const renderDateRange = (
    fromKey: keyof typeof localMtmFilters,
    toKey: keyof typeof localMtmFilters,
  ) => (
    <div className="grid grid-cols-1 gap-2">
      <div>
        <label className="block text-xs text-gray-500 mb-1">From</label>
        <input
          type="date"
          max={(localMtmFilters[toKey] as string) || undefined}
          value={(localMtmFilters[fromKey] as string) || ''}
          onChange={(e) => {
            const val = e.target.value || null;
            const toVal = localMtmFilters[toKey] as string | null;
            if (val && toVal && val > toVal) {
              updateFilter(fromKey, toVal);
            } else {
              updateFilter(fromKey, val);
            }
          }}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">To</label>
        <input
          type="date"
          min={(localMtmFilters[fromKey] as string) || undefined}
          value={(localMtmFilters[toKey] as string) || ''}
          onChange={(e) => {
            const val = e.target.value || null;
            const fromVal = localMtmFilters[fromKey] as string | null;
            if (val && fromVal && val < fromVal) {
              updateFilter(toKey, fromVal);
            } else {
              updateFilter(toKey, val);
            }
          }}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>
  );

  const renderNumericRange = (
    minKey: keyof typeof localMtmFilters,
    maxKey: keyof typeof localMtmFilters,
    step: string,
    minPlaceholder: string,
    maxPlaceholder: string,
  ) => (
    <NumericRangeFilter
      min={localMtmFilters[minKey] as number | null}
      max={localMtmFilters[maxKey] as number | null}
      onChange={(min, max) => setLocalMtmFilters({ ...localMtmFilters, [minKey]: min, [maxKey]: max })}
      step={step}
      minPlaceholder={minPlaceholder}
      maxPlaceholder={maxPlaceholder}
    />
  );

  const renderFilterContent = (filterId: string) => {
    switch (filterId) {
      case 'statuses':
        return renderMultiSelect('statuses', MOCK_STATUSES, 'Select statuses...', 'Search statuses...', TRADE_STATUS_LABELS);
      case 'symbols':
        return renderMultiSelect('symbols', securityOptions, 'Select securities...', 'Search securities...');
      case 'tradeTypes':
        return renderMultiSelect('tradeTypes', MOCK_TRADE_TYPES, 'Select trade types...', 'Search trade types...');
      case 'collateralTypes':
        return renderMultiSelect('collateralTypes', MOCK_COLLATERAL_TYPES, 'Select collateral types...', 'Search collateral types...', COLLATERAL_TYPE_LABELS);
      case 'agreementTypes':
        return renderMultiSelect('agreementTypes', MOCK_AGREEMENT_TYPES, 'Select agreement types...', 'Search agreement types...');
      case 'termTypes':
        return renderMultiSelect('termTypes', MOCK_TERM_TYPES, 'Select trade terms...', 'Search trade terms...');
      case 'currencies':
        return renderMultiSelect('currencies', currencyOptions, 'Select currencies...', 'Search currencies...');
      case 'tradeDate':
        return renderDateRange('tradeDateFrom', 'tradeDateTo');
      case 'settlementDate':
        return renderDateRange('settlementDateFrom', 'settlementDateTo');
      case 'maturityDate':
        return renderDateRange('maturityDateFrom', 'maturityDateTo');
      case 'quantity':
        return renderNumericRange('quantityMin', 'quantityMax', '1', '0', '1000000');
      case 'contractValue':
        return renderNumericRange('contractValueMin', 'contractValueMax', '0.01', '0', '10000000');
      case 'margin':
        return renderNumericRange('marginMin', 'marginMax', '0.01', '1', '199');
      case 'grossPrice':
        return renderNumericRange('grossPriceMin', 'grossPriceMax', '0.01', '0', '10000000');
      case 'dailyEarnings':
        return renderNumericRange('dailyEarningsMin', 'dailyEarningsMax', '0.01', '0', '1000000');
      case 'createdAt':
        return renderDateRange('createdAtFrom', 'createdAtTo');
      case 'updatedAt':
        return renderDateRange('updatedAtFrom', 'updatedAtTo');
      default:
        return null;
    }
  };

  const hasActiveFilters = activeFilterCount > 0;

  const headerContent = (
    <div className="flex items-center justify-between w-full ml-4">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => handleTabChange('filters')}
          className={clsx(
            'px-2.5 py-1 text-base font-semibold transition-colors',
            activeTab === 'filters'
              ? 'text-gray-900 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Filters
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('columns')}
          className={clsx(
            'px-2.5 py-1 text-base font-semibold transition-colors',
            activeTab === 'columns'
              ? 'text-gray-900 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Columns
        </button>
      </div>
    </div>
  );

  return (
    <BaseFilterSidebar
      activeFilterCount={activeFilterCount}
      onClearAll={clearAllFilters}
      headerContent={headerContent}
    >
      {/* Columns tab content */}
      {!filterSidebarCollapsed && activeTab === 'columns' && (
        <div className="flex flex-1 flex-col -mt-2 min-h-0">
          <MtmColumnManagerPanel />
        </div>
      )}

      {/* Filters tab content */}
      {!filterSidebarCollapsed && activeTab === 'filters' && (
        <>
          {hasActiveFilters && (
            <div className="flex justify-start -my-2 -ml-2.5 -mt-4">
              <button
                onClick={clearAllFilters}
                className="text-xs font-medium rounded px-2 py-1 transition-colors text-red-600 hover:text-red-800"
              >
                Clear All
              </button>
            </div>
          )}
          {(() => {
              const collapsibleIds = FILTER_DEFS.map((f) => f.id);
              const allExpanded = FILTER_DEFS.every(
                (f) => expandedFilterIds.includes(f.id) || hasActiveFilter(f.id)
              );
              return (
                <div className="flex items-center justify-between -mt-2 mb-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (allExpanded) {
                        collapseAllFilters();
                      } else {
                        expandAllFilters(collapsibleIds);
                      }
                    }}
                    className="text-xs font-medium text-blue-600 hover:text-blue-800"
                  >
                    {allExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>
              );
            })()}
          {sortFiltersByColumnOrder(FILTER_DEFS, columnConfig).map((filter) => {
            const active = hasActiveFilter(filter.id);
            const expanded = expandedFilterIds.includes(filter.id) || active;
            const content = renderFilterContent(filter.id);
            if (!content) return null;

            const columnId = MTM_FILTER_ID_TO_COLUMN_ID[filter.id] ?? null;
            const isColumnVisible = columnId
              ? columnConfig.some((c) => c.id === columnId && c.visible)
              : false;

            return (
              <CollapsibleFilterSection
                key={filter.id}
                filterId={filter.id}
                label={filter.label}
                expanded={expanded}
                onToggle={() => (expanded && !active ? collapseFilter(filter.id) : expandFilter(filter.id))}
                isFocused={focusedFilterId === filter.id}
                hasActiveValues={active}
                columnId={columnId}
                onLocateColumn={(colId) => setLocateColumnRequest({ columnId: colId, trigger: Date.now() })}
                isColumnVisible={isColumnVisible}
              >
                {content}
              </CollapsibleFilterSection>
            );
          })}
          <div>
            <button
              onClick={handleExport}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export as CSV
            </button>
            <p className="mt-2 text-xs text-gray-500 text-center">
              {exportableCount} row{exportableCount !== 1 ? 's' : ''} will be exported
              {/* LR-548 put callable repo/reverse rows in the export basis too, so the old
                  "lend/borrow only" note described a scope the file no longer has. */}
              <span className="block text-[11px] text-gray-400">
                checked rows in view — lend/borrow + repo in the margin call
              </span>
            </p>
          </div>
        </>
      )}
    </BaseFilterSidebar>
  );
};
